import React, { useMemo } from "react";

import { Content } from "./components/Content";
import { NeighborhoodMapProvider } from "./context";
import { schema } from "./schema";
import { NeighborhoodMapProps } from "./types";

import { TrackingProvider, initializeAnalytics } from "~/tracking";

// TODO Move this import to sdks/sdks-app/src/App.tsx once all SDKs are migrated
import "~/assets/styles/globals.css";

import { ValidateOptions } from "~/components/ValidateOptions";
import { LocalLogicProvider } from "~/context/LocalLogicProvider";
import { useStateManagerContext } from "~/context/stateManager";
import useIsMobile from "~/hooks/useIsMobile";
import { ProductNames } from "~/types";
import { ANALYTICS_SETTINGS } from "~/constants/analytics";

initializeAnalytics(ANALYTICS_SETTINGS.neighborhoodWrap.key);

const NeighborhoodMap = () => {
  const { sdkState } = useStateManagerContext<NeighborhoodMapProps>();
  const isMobile = useIsMobile();
  const locale = sdkState.globalOptions?.locale || "en";

  const location = useMemo(() => {
    if (sdkState.options?.geographyId) {
      return {
        geographyId: sdkState.options?.geographyId,
      };
    }

    return {
      lat: sdkState.options?.lat,
      lng: sdkState.options?.lng,
    };
  }, [
    sdkState.options?.lat,
    sdkState.options?.lng,
    sdkState.options?.geographyId,
  ]);

  return (
    <ValidateOptions
      sdk="Neighborhood Map"
      schema={schema}
      options={sdkState.options!}
    >
      <LocalLogicProvider
        environment={sdkState.globalOptions?.environment?.api}
        apiKey={sdkState.apiKey}
        appearance={sdkState.globalOptions?.appearance}
        locale={sdkState.globalOptions?.locale}
        product={ProductNames.NEIGHBORHOOD_MAP}
      >
        <TrackingProvider
          trackingPipelineSettings={{
            apiKey: sdkState.apiKey || "",
          }}
          analyticsSettings={ANALYTICS_SETTINGS.neighborhoodWrap}
          meta={{
            clientId: sdkState.clientId,
            productName: ProductNames.NEIGHBORHOOD_MAP,
            productVersion: FULL_APP_VERSION,
          }}
          attributes={{
            location,
            host: sdkState.location,
            host_url: document.referrer,
            locale,
            isMobile,
          }}
          flags={{
            isDebugMode:
              import.meta.env.VITE_TRACKING_PIPELINE_DEBUG === "true",
          }}
        >
          <NeighborhoodMapProvider>
            <Content />
          </NeighborhoodMapProvider>
        </TrackingProvider>
      </LocalLogicProvider>
    </ValidateOptions>
  );
};

export default NeighborhoodMap;
